import { ApiClient } from "./apiClient";
import { ApiResponse } from "./types";
import { appConfig } from "../config";
import {AttributeFilter, FilterOperator} from "./EmployeeApi";


const analyticsApiClient = new ApiClient(`${appConfig.backendUrl}/api/reports`);


// HERE

/**
 * Report Configuration Interfaces
 *
 * These interfaces define the structure for report configurations
 * that can be saved to and loaded from the backend.
 */

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Base filter condition
 */
export interface FilterCondition {
    /** The attribute to filter on */
    attributeName: string;

    /** The filter operation (equals, contains, greaterThan, etc.) */
    operator: FilterOperator;

    /** The value to filter by (can be single value, array, or range) */
    value: FilterValue;

    /** Optional display label for the filter */
    label?: string;
}

// /**
//  * Supported filter operators
//  */
// export enum FilterOperator {
//     // Comparison
//     EQUALS = 'equals',
//     NOT_EQUALS = 'notEquals',
//
//     // Numeric
//     GREATER_THAN = 'greaterThan',
//     LESS_THAN = 'lessThan',
//     GREATER_THAN_OR_EQUALS = 'greaterThanOrEquals',
//     LESS_THAN_OR_EQUALS = 'lessThanOrEquals',
//     BETWEEN = 'between',
//
//     // String
//     CONTAINS = 'contains',
//     NOT_CONTAINS = 'notContains',
//     STARTS_WITH = 'startsWith',
//     ENDS_WITH = 'endsWith',
//
//     // Set operations
//     IN = 'in',
//     NOT_IN = 'notIn',
//
//     // Date
//     BEFORE = 'before',
//     AFTER = 'after',
//     IN_LAST = 'inLast',
//     IN_NEXT = 'inNext',
//
//     // Null checks
//     IS_EMPTY = 'isEmpty',
//     IS_NOT_EMPTY = 'isNotEmpty'
// }

/**
 * Filter value types
 */
export type FilterValue =
    | string
    | number
    | boolean
    | Date
    | string[]
    | number[]
    | FilterRangeValue
    | FilterRelativeDateValue
    | null;

/**
 * Range value for between operations
 */
export interface FilterRangeValue {
    min: number | Date;
    max: number | Date;
}

/**
 * Relative date value (e.g., "last 30 days")
 */
export interface FilterRelativeDateValue {
    amount: number;
    unit: 'days' | 'weeks' | 'months' | 'quarters' | 'years';
}

/**
 * Logical grouping of filters
 */
export interface FilterGroup {
    /** Logical operator to combine filters */
    operator: 'AND' | 'OR';

    /** Array of filter conditions or nested filter groups */
    conditions: (FilterCondition | FilterGroup)[];
}

// ============================================================================
// GROUPING INTERFACES
// ============================================================================

/**
 * Grouping configuration
 */
export interface GroupingConfig {
    /** The attribute to group by */
    attributeName: string;

    /** Grouping mode (value, range, firstLetter, etc.) */
    mode: GroupingMode;

    /** Configuration specific to the grouping mode */
    modeConfig?: GroupingModeConfig;

    /** Sort order for groups */
    sortOrder?: SortOrder;

    /** Optional display label */
    label?: string;
}

/**
 * Grouping modes
 */
export enum GroupingMode {
    VALUE = 'value',
    RANGE = 'range',
    FIRST_LETTER = 'firstLetter',
    DOMAIN = 'domain',
    PREFIX = 'prefix',
    HIERARCHY = 'hierarchy',
    CATEGORY = 'category',
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month',
    QUARTER = 'quarter',
    YEAR = 'year',
    REGION = 'region'
}

/**
 * Configuration specific to grouping modes
 */
export type GroupingModeConfig =
    | RangeGroupingConfig
    | PrefixGroupingConfig
    | CategoryGroupingConfig
    | CustomRangeGroupingConfig;

/**
 * Configuration for numeric range grouping
 */
export interface RangeGroupingConfig {
    type: 'range';
    /** Size of each range bucket */
    bucketSize: number;
    /** Optional min value */
    min?: number;
    /** Optional max value */
    max?: number;
}

/**
 * Configuration for custom range grouping
 */
export interface CustomRangeGroupingConfig {
    type: 'customRange';
    /** Custom range definitions */
    ranges: Array<{
        label: string;
        min: number;
        max: number;
    }>;
}

/**
 * Configuration for prefix grouping (e.g., postal codes)
 */
export interface PrefixGroupingConfig {
    type: 'prefix';
    /** Number of characters to use as prefix */
    length: number;
}

/**
 * Configuration for category grouping
 */
export interface CategoryGroupingConfig {
    type: 'category';
    /** Mapping of values to categories */
    categories: Record<string, string[]>;
}

// ============================================================================
// AGGREGATION INTERFACES
// ============================================================================

/**
 * Aggregation configuration
 */
export interface AggregationConfig {
    /** The aggregation function to apply */
    function: AggregationFunction;

    /** The attribute to aggregate (for field-based aggregations) */
    attributeName?: string;

    /** Optional display label */
    label?: string;

    /** Format settings for the result */
    format?: AggregationFormat;
}

/**
 * Supported aggregation functions
 */
export enum AggregationFunction {
    // Count
    COUNT = 'COUNT',
    COUNT_DISTINCT = 'COUNT_DISTINCT',

    // Numeric
    SUM = 'SUM',
    AVG = 'AVG',
    MIN = 'MIN',
    MAX = 'MAX',
    MEDIAN = 'MEDIAN',
    STD_DEV = 'stdDev',

    // Percentage
    PERCENTAGE = 'percentage',

    // Custom
    TENURE = 'tenure',
    SPAN_OF_CONTROL = 'spanOfControl'
}

/**
 * Format settings for aggregation results
 */
export interface AggregationFormat {
    /** Number format (decimal, integer, percentage) */
    type: 'decimal' | 'integer' | 'percentage' | 'currency';

    /** Number of decimal places */
    decimals?: number;

    /** Thousands separator */
    thousandsSeparator?: boolean;

    /** Currency code (for currency format) */
    currencyCode?: string;
}

// ============================================================================
// SORTING INTERFACES
// ============================================================================

/**
 * Sort configuration
 */
export interface SortConfig {
    /** Field to sort by (can be attribute or aggregation result) */
    field: string;

    /** Sort order */
    order: SortOrder;
}

/**
 * Sort order
 */
export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc'
}

// ============================================================================
// VISUALIZATION INTERFACES
// ============================================================================

/**
 * Visualization configuration
 */
export interface VisualizationConfig {
    /** Chart type */
    type: ChartType;

    /** Chart-specific configuration */
    chartConfig?: ChartConfig;

    /** Color scheme */
    colorScheme?: ColorScheme;

    /** Display options */
    displayOptions?: DisplayOptions;
}

/**
 * Supported chart types
 */
export enum ChartType {
    BAR = 'BAR',
    TREE_MAP = 'TREE_MAP',
    COLUMN = 'column',
    LINE = 'line',
    PIE = 'pie',
    DONUT = 'donut',
    AREA = 'area',
    SCATTER = 'scatter',
    TABLE = 'table',
    CARD = 'card'
}

/**
 * Chart-specific configuration
 */
export type ChartConfig =
    | BarChartConfig
    | LineChartConfig
    | PieChartConfig
    | TableConfig
    | CardConfig;

/**
 * Bar/Column chart configuration
 */
export interface BarChartConfig {
    type: 'bar' | 'column';
    /** Stack bars */
    stacked?: boolean;
    /** Show data labels */
    showDataLabels?: boolean;
    /** Show legend */
    showLegend?: boolean;
    /** Horizontal orientation (for bar charts) */
    horizontal?: boolean;
}

/**
 * Line/Area chart configuration
 */
export interface LineChartConfig {
    type: 'line' | 'area';
    /** Smooth curves */
    smooth?: boolean;
    /** Show data points */
    showPoints?: boolean;
    /** Show data labels */
    showDataLabels?: boolean;
    /** Show legend */
    showLegend?: boolean;
}

/**
 * Pie/Donut chart configuration
 */
export interface PieChartConfig {
    type: 'pie' | 'donut';
    /** Show percentages */
    showPercentages?: boolean;
    /** Show labels */
    showLabels?: boolean;
    /** Show legend */
    showLegend?: boolean;
}

/**
 * Table configuration
 */
export interface TableConfig {
    type: 'table';
    /** Show totals row */
    showTotals?: boolean;
    /** Enable pagination */
    pagination?: boolean;
    /** Rows per page */
    rowsPerPage?: number;
    /** Enable column sorting */
    sortable?: boolean;
}

/**
 * Card/KPI configuration
 */
export interface CardConfig {
    type: 'card';
    /** Show comparison (vs previous period) */
    showComparison?: boolean;
    /** Show trend indicator */
    showTrend?: boolean;
    /** Comparison period */
    comparisonPeriod?: ComparisonPeriod;
}

/**
 * Comparison period for KPI cards
 */
export interface ComparisonPeriod {
    type: 'previous' | 'custom';
    /** For custom comparison */
    filterGroup?: FilterGroup;
}

/**
 * Color scheme
 */
export interface ColorScheme {
    /** Predefined color palette name */
    palette?: string;

    /** Custom colors */
    colors?: string[];

    /** Color mapping for specific values */
    valueColors?: Record<string, string>;
}

/**
 * Display options
 */
export interface DisplayOptions {
    /** Chart title */
    title?: string;

    /** Chart description */
    description?: string;

    /** Show title */
    showTitle?: boolean;

    /** Show description */
    showDescription?: boolean;

    /** Chart width (in pixels or percentage) */
    width?: number | string;

    /** Chart height (in pixels) */
    height?: number;

    /** X-axis label */
    xAxisLabel?: string;

    /** Y-axis label */
    yAxisLabel?: string;

    /** Show grid */
    showGrid?: boolean;
}

// ============================================================================
// REPORT CONFIGURATION INTERFACES
// ============================================================================

/**
 * Complete report configuration
 */
export interface ReportConfig {
    /** Unique identifier (generated by backend) */
    id?: string;

    /** Report name */
    name: string;

    /** Report description */
    description?: string;

    /** Entity type (e.g., 'Employee', 'Candidate') */
    entityType: string;

    /** Main attribute to report on */
    primaryAttribute: string;

    /** Filter configuration */
    filters?: FilterGroup;

    /** Grouping configuration (can have multiple levels) */
    grouping?: GroupingConfig[];

    /** Aggregation configuration (can have multiple metrics) */
    aggregations: AggregationConfig[];

    /** Sort configuration */
    sorting?: SortConfig[];

    /** Visualization configuration */
    visualization: VisualizationConfig;

    /** Report metadata */
    metadata?: ReportMetadata;

    /** Access control */
    access?: ReportAccess;

    /** Schedule configuration */
    schedule?: ReportSchedule;
}

/**
 * Report metadata
 */
export interface ReportMetadata {
    /** Report category/folder */
    category?: string;

    /** Tags for organization */
    tags?: string[];

    /** Creator user ID */
    createdBy?: string;

    /** Creation timestamp */
    createdAt?: Date;

    /** Last modifier user ID */
    modifiedBy?: string;

    /** Last modification timestamp */
    modifiedAt?: Date;

    /** Is this a template */
    isTemplate?: boolean;

    /** Is this report featured */
    isFeatured?: boolean;
}

/**
 * Report access control
 */
export interface ReportAccess {
    /** Visibility level */
    visibility: 'private' | 'shared' | 'public';

    /** User IDs with view access */
    viewers?: string[];

    /** User IDs with edit access */
    editors?: string[];

    /** Role-based access */
    roleAccess?: {
        role: string;
        permissions: ('view' | 'edit' | 'delete')[];
    }[];
}

/**
 * Report schedule configuration
 */
export interface ReportSchedule {
    /** Is scheduling enabled */
    enabled: boolean;

    /** Frequency */
    frequency: ScheduleFrequency;

    /** Time of day to run (HH:MM format) */
    timeOfDay?: string;

    /** Day of week (for weekly) */
    dayOfWeek?: number;

    /** Day of month (for monthly) */
    dayOfMonth?: number;

    /** Recipients (email addresses or user IDs) */
    recipients: string[];

    /** Email subject template */
    emailSubject?: string;

    /** Email body template */
    emailBody?: string;

    /** Export format */
    exportFormat: 'pdf' | 'xlsx' | 'csv' | 'png';
}

/**
 * Schedule frequency
 */
export enum ScheduleFrequency {
    DAILY = 'daily',
    WEEKLY = 'weekly',
    MONTHLY = 'monthly',
    QUARTERLY = 'quarterly',
    CUSTOM = 'custom'
}

// ============================================================================
// SAVED REPORT (BACKEND RESPONSE)
// ============================================================================

/**
 * Saved report (returned from backend)
 */
export interface SavedReport extends ReportConfig {
    /** Unique identifier */
    id: string;

    /** Metadata */
    metadata: Required<ReportMetadata>;

    /** Version number */
    version: number;

    /** Is this report archived */
    isArchived: boolean;

    /** Last execution timestamp */
    lastExecutedAt?: Date;

    /** Execution count */
    executionCount?: number;
}

// ============================================================================
// REPORT EXECUTION INTERFACES
// ============================================================================

/**
 * Report execution request
 */
export interface ReportExecutionRequest {
    /** Report configuration ID */
    reportId?: string;

    /** Or inline report configuration */
    config?: ReportConfig;

    /** Override filters for this execution */
    filterOverrides?: FilterGroup;

    /** Date range for this execution */
    dateRange?: {
        start: Date;
        end: Date;
    };

    /** Limit number of results */
    limit?: number;

    /** Offset for pagination */
    offset?: number;
}

/**
 * Report execution result
 */
export interface ReportExecutionResult {
    /** Execution ID */
    executionId: string;

    /** Report configuration used */
    config: ReportConfig;

    /** Result data */
    // data: ReportData;
    data: Record<string, Record<string, number>>;

    groupings: string[];

    /** Execution metadata */
    metadata: {
        executedAt: Date;
        executedBy: string;
        executionTime: number; // milliseconds
        rowCount: number;
        filteredRowCount?: number;
    };
}

/**
 * Report data structure
 */
export interface ReportData {
    /** Column definitions */
    columns: ReportColumn[];

    /** Data rows */
    rows: ReportRow[];

    /** Summary/totals row */
    totals?: ReportRow;

    /** Additional metadata */
    metadata?: {
        groupByAttribute?: string;
        aggregations?: string[];
        hasMoreData?: boolean;
    };
}

/**
 * Report column definition
 */
export interface ReportColumn {
    /** Column ID */
    id: string;

    /** Display label */
    label: string;

    /** Data type */
    dataType: 'string' | 'number' | 'date' | 'boolean';

    /** Format */
    format?: AggregationFormat;

    /** Is this column sortable */
    sortable?: boolean;

    /** Column width (for table display) */
    width?: number;
}

/**
 * Report row data
 */
export interface ReportRow {
    /** Row ID */
    id: string;

    /** Cell values (keyed by column ID) */
    values: Record<string, any>;

    /** Nested rows (for hierarchical grouping) */
    children?: ReportRow[];
}

// ============================================================================
// REPORT TEMPLATE INTERFACES
// ============================================================================

/**
 * Report template for quick report creation
 */
export interface ReportTemplate {
    /** Template ID */
    id: string;

    /** Template name */
    name: string;

    /** Template description */
    description: string;

    /** Template category */
    category: string;

    /** Icon or preview image */
    icon?: string;

    /** Base configuration */
    config: Partial<ReportConfig>;

    /** Required user inputs */
    parameters?: ReportParameter[];
}

/**
 * Report parameter (for templates)
 */
export interface ReportParameter {
    /** Parameter ID */
    id: string;

    /** Display label */
    label: string;

    /** Parameter type */
    type: 'attribute' | 'filter' | 'dateRange' | 'custom';

    /** Is this parameter required */
    required: boolean;

    /** Default value */
    defaultValue?: any;

    /** Options (for select parameters) */
    options?: Array<{
        value: any;
        label: string;
    }>;
}

// HERE



export interface Report {
    id: string;
    reportName: string;
    reportDescription?: string;
    configuration: ReportConfiguration;
    createdAt: string;
}

export interface ReportConfiguration {
    groupingAttribute: string;
    aggregationFunction: AggregationFunction;
    attributeFilters?: AttributeFilter[];
}


// ============================================================================
// Transfer objects
// ============================================================================

type ReportBaseRequest = {
    groupingEntity: string;
    groupingAttribute: string;
    groupingNestedAttribute?: string;
    aggregationFunction: AggregationFunction;
    attributeFilters?: AttributeFilter[];
    visualization: VisualizationConfig;
    from?: string;
    to?: string;
};

export type ReportPreviewRequest = ReportBaseRequest;

export type ReportCreateRequest = ReportBaseRequest & {
    reportName: string;
    reportDescription?: string;
};

// ============================================================================
// API Methods
// ============================================================================

export const fetchAllReports = ():
    Promise<ApiResponse<Report[]>> => {

    return analyticsApiClient.get('');
};

export const createReport = (request: ReportCreateRequest):
    Promise<ApiResponse<Report>> => {

    return analyticsApiClient.post(``, request);
};

export const deleteReport = (id: string): Promise<ApiResponse<void>> => {
    return analyticsApiClient.delete(`/${id}`);
};

export const executeReport = (reportId: string):
    Promise<ApiResponse<ReportExecutionResult>> => {

    return analyticsApiClient.post(`/${reportId}/execute`, {});
};

export const previewReport = (request: ReportPreviewRequest):
    Promise<ApiResponse<ReportExecutionResult>> => {

    return analyticsApiClient.post(`/preview`, request);
};